import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listUsers, type UserSummary } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CreateUserModal } from "../components/CreateUserModal";
import { EditUserModal } from "../components/EditUserModal";
import { UserSessionsDialog } from "../components/UserSessionsDialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";

type Filter = "all" | "admins" | "active";

function fmt(ts: string | null): string {
  if (!ts) return "—";
  return ts.replace("T", " ").slice(0, 19);
}

export function AdminUsersRoute() {
  const { me } = useAuth();
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [revoking, setRevoking] = useState<UserSummary | null>(null);

  const filters = (() => {
    if (filter === "admins") return { is_admin: true };
    if (filter === "active") return { is_active: true };
    return undefined;
  })();

  const users = useQuery({
    queryKey: ["admin", "users", filter],
    queryFn: () => listUsers(filters),
  });

  if (!me?.is_admin) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm">
        Kein Admin-Zugriff.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Users</CardTitle>
          <CreateUserModal />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            {(
              [
                { id: "all", label: "Alle" },
                { id: "active", label: "Aktiv" },
                { id: "admins", label: "Admins" },
              ] as { id: Filter; label: string }[]
            ).map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={filter === f.id ? "default" : "outline"}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          {users.isLoading && <p className="text-sm">Lade…</p>}
          {users.isError && (
            <p className="text-sm text-destructive">
              Konnte Users nicht laden.
            </p>
          )}
          {users.data && users.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Keine User im gewählten Filter.
            </p>
          )}
          {users.data && users.data.length > 0 && (
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Rollen</th>
                    <th className="px-3 py-2">Erstellt</th>
                    <th className="px-3 py-2">Last login</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {users.data.map((u) => (
                    <tr key={u.id} className="border-t">
                      <td className="px-3 py-2 font-medium">{u.email}</td>
                      <td className="px-3 py-2">{u.display_name}</td>
                      <td className="px-3 py-2">
                        {u.is_admin && (
                          <span className="mr-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                            Admin
                          </span>
                        )}
                        <span
                          className={
                            u.is_active
                              ? "rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800"
                              : "rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                          }
                        >
                          {u.is_active ? "Aktiv" : "Deaktiviert"}
                        </span>
                      </td>
                      <td className="px-3 py-2">{fmt(u.created_at)}</td>
                      <td className="px-3 py-2">{fmt(u.last_login_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(u)}
                          className="mr-2"
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setRevoking(u)}
                        >
                          Sessions widerrufen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EditUserModal user={editing} onClose={() => setEditing(null)} />
      <UserSessionsDialog
        user={revoking}
        onClose={() => setRevoking(null)}
      />
    </div>
  );
}
